import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

export default function TattooAftercareAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => { let tag = document.getElementById(id); if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); } tag.textContent = JSON.stringify(s); };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Φροντίδα Τατουάζ Μετά το Studio",
      "description": "Πλήρεις οδηγίες aftercare τατουάζ από το 210 Tattoo Athens. Γρήγορη επούλωση, διατήρηση χρώματος και αποφυγή μολύνσεων.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-03-25",
      "dateModified": "2026-03-25",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/tattoo-aftercare-athens",
      "inLanguage": "el"
    };
    inject('schema-aftercare-article', articleSchema);
    return () => {
      document.getElementById('schema-aftercare-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Φροντίδα Τατουάζ Μετά το Studio | 210 Tattoo Athens</title>
        <meta name="description" content="Πλήρεις οδηγίες aftercare τατουάζ από το 210 Tattoo Athens. Γρήγορη επούλωση, διατήρηση χρώματος και αποφυγή μολύνσεων." />
        <link rel="canonical" href="https://210tattoo.gr/blog/tattoo-aftercare-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/tattoo-aftercare-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/tattoo-aftercare-athens" />
        <meta property="og:title" content="Φροντίδα Τατουάζ Μετά το Studio | 210 Tattoo Athens" />
        <meta property="og:description" content="Πλήρεις οδηγίες aftercare τατουάζ από το 210 Tattoo Athens. Γρήγορη επούλωση, διατήρηση χρώματος και αποφυγή μολύνσεων." />
        <meta property="og:url" content="https://210tattoo.gr/blog/tattoo-aftercare-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('tattoo-aftercare-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Φροντίδα Τατουάζ Μετά το Studio | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Πλήρεις οδηγίες aftercare τατουάζ από το 210 Tattoo Athens. Γρήγορη επούλωση, διατήρηση χρώματος." />
        <meta name="twitter:image" content={ogImage('tattoo-aftercare-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Tattoo Aftercare Athens</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Aftercare Τατουάζ</span>
              <br />
              <span className="text-white">Μετά το Studio</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">25 Μαρτίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Βγήκατε μόλις από το studio του 210 Tattoo Athens και θέλετε να ξέρετε ακριβώς τι να κάνετε τώρα; Αυτός ο οδηγός καλύπτει τα πάντα από τις πρώτες ώρες μέχρι τις πρώτες εβδομάδες. Σωστό aftercare σημαίνει καλύτερο αποτέλεσμα, ταχύτερη επούλωση και τατουάζ που θα διατηρηθεί για δεκαετίες.
            </p>
          </div>
        </section>

        {/* Why aftercare matters */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Γιατί το Aftercare <span className="text-pink-500">Είναι Τόσο Σημαντικό</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-4">
              Ένα φρέσκο τατουάζ είναι ουσιαστικά ανοιχτή πληγή. Το δέρμα έχει τρυπηθεί χιλιάδες φορές από τη βελόνα του μηχανήματος και τώρα χρειάζεται χρόνο για να αναδομηθεί. Όσο πιο καλά φροντίσετε αυτή τη διαδικασία, τόσο πιο καθαρό, έντονο και σαφές θα μείνει το τατουάζ σας.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-4">
              Αντίθετα, κακή φροντίδα μπορεί να οδηγήσει σε απώλεια χρώματος, θολές γραμμές, ουλές ή ακόμα και μόλυνση που απαιτεί ιατρική αντιμετώπιση. Τα καλύτερα τατουάζ αθήνα είναι αυτά που συνδυάζουν ποιοτική εκτέλεση με σωστή φροντίδα.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Τα φρέσκα τατουάζ αντιδρούν επίσης διαφορετικά ανάλογα με την εποχή. Το καλοκαίρι στην Αθήνα, ο συνδυασμός ζέστης, ιδρώτα και έντονης ηλιοφάνειας κάνει το aftercare πιο απαιτητικό. Τον χειμώνα, η ξηρότητα του αέρα απαιτεί συχνότερη ενυδάτωση.
            </p>
          </div>
        </section>

        {/* Hour by hour first day */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-10">Οι Πρώτες <span className="text-pink-500">24 Ώρες</span></h2>
            <div className="space-y-6">
              {[
                {
                  step: '00',
                  title: 'Στο studio',
                  desc: 'Το τατουάζ καλύπτεται με αποστειρωμένο επίδεσμο ή second skin. Αν χρησιμοποιηθεί second skin (διαφανές αυτοκόλλητο), το αφήνουμε για 3 με 5 μέρες χωρίς να το αγγίξετε. Αν είναι κλασικός επίδεσμος, αφαιρείται σε 2 με 4 ώρες.',
                },
                {
                  step: '02',
                  title: 'Πρώτο πλύσιμο',
                  desc: 'Αφαιρέστε τον επίδεσμο με καθαρά χέρια. Πλύνετε το τατουάζ με χλιαρό νερό και ήπιο, άοσμο σαπούνι. Μην χρησιμοποιείτε σφουγγάρι ή loofa. Ξεπλύνετε καλά και στεγνώστε με σκούπισμα ή αφήστε να στεγνώσει στον αέρα.',
                },
                {
                  step: '04',
                  title: 'Πρώτη εφαρμογή κρέμας',
                  desc: 'Εφαρμόστε πολύ λεπτό στρώμα αδιάρρηκτης ενυδατικής κρέμας. Η κρέμα πρέπει να απλώνεται και να απορροφάται, όχι να μένει σαν στρώμα πάνω από το τατουάζ. Παραδείγματα κατάλληλων κρεμών: Bepanthen, Dr. Pickles, Tattoo Goo, ή αδιάρρηκτη baby cream.',
                },
                {
                  step: '12',
                  title: 'Επανάληψη',
                  desc: 'Επαναλάβετε το πλύσιμο και την εφαρμογή κρέμας πριν κοιμηθείτε. Φορέστε καθαρό, φαρδύ ρούχο πάνω από το τατουάζ αν κοιμάστε σε κλινοσκεπάσματα που μπορεί να κολλήσουν στο φρέσκο δέρμα.',
                },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}h</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Week by week */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-10">Εβδομάδα <span className="text-pink-500">προς Εβδομάδα</span></h2>
            <div className="space-y-6">
              {[
                {
                  step: '01',
                  title: 'Εβδομάδα 1',
                  desc: 'Πλύσιμο δύο φορές την ημέρα, ενυδάτωση 3 φορές. Το δέρμα θα είναι ευαίσθητο και μπορεί να φαίνεται κόκκινο. Αποφύγετε τη θάλασσα, την πισίνα, το γυμναστήριο με έντονη εφίδρωση και τον άμεσο ήλιο. Κάντε κανονικό ντους αλλά μην κατευθύνετε τη ζεστή ρίγα νερού κατευθείαν στο τατουάζ.',
                },
                {
                  step: '02',
                  title: 'Εβδομάδα 2',
                  desc: 'Αρχίζει το ξεφλούδισμα. Είναι φυσιολογικό και δείχνει ότι το δέρμα ανανεώνεται. Μην τραβάτε τα νεκρά κύτταρα. Η φαγούρα αυξάνεται αυτή την εβδομάδα. Χτυπήστε ελαφρά αντί να ξύνετε. Συνεχίστε την ενυδάτωση.',
                },
                {
                  step: '03',
                  title: 'Εβδομάδες 3 και 4',
                  desc: 'Εξωτερικά το τατουάζ φαίνεται έτοιμο. Τα βαθύτερα στρώματα του δέρματος συνεχίζουν να επουλώνονται για 4 με 6 εβδομάδες ακόμα. Συνεχίστε αντηλιακό σε κάθε έκθεση στον ήλιο. Αν εμφανιστούν περιοχές που φαίνονται θαμπές ή άδειες, περιμένετε να ολοκληρωθεί η επούλωση πριν κρίνετε αν χρειάζεται touch-up.',
                },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Products */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι Προϊόντα <span className="text-pink-500">να Χρησιμοποιήσετε</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Στην Ελλάδα βρίσκετε εύκολα κατάλληλα προϊόντα σε φαρμακεία και σούπερ μάρκετ. Δεν χρειάζεστε ακριβά ειδικά προϊόντα αν δεν τα βρείτε.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Σαπούνι', desc: 'Οποιοδήποτε ήπιο, άοσμο σαπούνι. Καλές επιλογές: Dove unscented, Neutrogena, baby soap. Αποφύγετε αντιβακτηριακά σαπούνια με αλκοόλ.' },
                { title: 'Ενυδατική κρέμα', desc: 'Bepanthen (βρίσκεται σε κάθε φαρμακείο), Dr. Pickles, Tattoo Goo, ή απλή baby cream χωρίς άρωμα. Η ποσότητα πρέπει να είναι μικρή, δεν πρέπει να "πνίγετε" το τατουάζ.' },
                { title: 'Αντηλιακό', desc: 'SPF 50 ή παραπάνω, κατά προτίμηση mineral. Χρησιμοποιείτε μετά την πλήρη επούλωση (4 εβδομάδες) σε κάθε έκθεση στον ήλιο. Σε Αθήνα τους καλοκαιρινούς μήνες, αυτό δεν είναι προαιρετικό.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What to avoid */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι να <span className="text-pink-500">Αποφύγεις Οπωσδήποτε</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Το aftercare έχει εξίσου σημασία με αυτό που κάνεις και με αυτό που δεν κάνεις. Οι παρακάτω κατηγορίες είναι οι πιο συχνές αιτίες προβλημάτων στην επούλωση.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Θάλασσα και πισίνα', desc: 'Τουλάχιστον 3 με 4 εβδομάδες αποχή. Το αλατόνερο, ο χλώριος και η άμμος ερεθίζουν το φρέσκο δέρμα και αυξάνουν τον κίνδυνο μόλυνσης και αποχρωματισμού.' },
                { title: 'Άμεσος ήλιος', desc: 'Ο ήλιος είναι ο κύριος εχθρός τόσο κατά την επούλωση όσο και μακροπρόθεσμα. Στην Αθήνα, αυτό σημαίνει αντηλιακό SPF 50+ σε κάθε έκθεση μόλις το τατουάζ επουλωθεί πλήρως.' },
                { title: 'Ξύσιμο και τράβηγμα', desc: 'Η φαγούρα που εμφανίζεται στη δεύτερη εβδομάδα είναι έντονη αλλά πρέπει να ανθίστασαι. Το ξύσιμο μπορεί να τραβήξει μελάνι ή να δημιουργήσει ουλές. Χτυπά ελαφρά αντί να ξύνεσαι.' },
                { title: 'Υπερβολική ενυδάτωση', desc: 'Πολλή κρέμα εμποδίζει το δέρμα να αναπνεύσει. Η κρέμα πρέπει να απορροφάται πλήρως, όχι να παραμένει σαν στρώμα. Αν το τατουάζ φαίνεται λιπαρό, έχεις βάλει πολλή.' },
                { title: 'Ρούχα που τρίβουν', desc: 'Σφιχτά ή τραχιά υφάσματα πάνω σε φρέσκο τατουάζ αυξάνουν τον ερεθισμό. Κατά προτίμηση φαρδιά, βαμβακερά ρούχα τις πρώτες εβδομάδες.' },
                { title: 'Αλκοόλ σε κρέμα ή σαπούνι', desc: 'Προϊόντα με αλκοόλ ξηραίνουν υπερβολικά το φρέσκο δέρμα. Έλεγχε πάντα τα συστατικά, ειδικά με αντιβακτηριακά σαπούνια που συχνά το περιέχουν.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Long-term care */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Μακροπρόθεσμη <span className="text-pink-500">Φροντίδα</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Η καλή εικόνα ενός τατουάζ μετά από 5 ή 10 χρόνια δεν είναι τυχαία. Εξαρτάται κατά μεγάλο μέρος από τον τρόπο ζωής και τη διατήρησή του με τον καιρό.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Αντηλιακό σε κάθε έκθεση', desc: 'Αυτό είναι το πιο σημαντικό μακροπρόθεσμο βήμα. Η UV ακτινοβολία διασπά το μελάνι και ξεθωριάζει το τατουάζ πιο γρήγορα από οτιδήποτε άλλο. Στην Αθήνα, αυτό είναι ιδιαίτερα κρίσιμο από Απρίλιο έως Οκτώβριο.' },
                { title: 'Ενυδάτωση του δέρματος', desc: 'Το καλά ενυδατωμένο δέρμα κρατά τα τατουάζ πιο ζωντανά. Μια κανονική ενυδατική ρουτίνα, χωρίς αυτό να αφορά ειδικά προϊόντα τατουάζ, κάνει σημαντική διαφορά.' },
                { title: 'Touch-up αν χρειαστεί', desc: 'Μετά από χρόνια, ορισμένες περιοχές μπορεί να χάσουν έντονο χρώμα ή καθαρότητα. Ένα touch-up είναι φυσιολογικό και συνιστάται να γίνεται από τον ίδιο artist που έκανε το αρχικό τατουάζ.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-10">Συχνές Ερωτήσεις <span className="text-pink-500">Aftercare</span></h2>
            <div className="space-y-4">
              {[
                { q: 'Πόσο διαρκεί η επούλωση;', a: 'Εξωτερικά 3 με 4 εβδομάδες, εσωτερικά 2 με 3 μήνες. Μεγαλύτερα τατουάζ παίρνουν περισσότερο χρόνο.' },
                { q: 'Μπορώ να κάνω γυμναστήριο;', a: 'Περιμένετε τουλάχιστον 2 εβδομάδες. Ο ιδρώτας και η τριβή μπορεί να επηρεάσουν αρνητικά τη φρέσκια επούλωση. Αποφύγετε ασκήσεις που φέρνουν ρούχα ή εξοπλισμό σε επαφή με το τατουάζ.' },
                { q: 'Μπορώ να κάνω ντους;', a: 'Ναι, από την πρώτη μέρα. Κανονικό ντους, αποφύγετε μόνο να μουλιάζετε το τατουάζ σε ζεστό νερό για πολλή ώρα και να κατευθύνετε ισχυρή ρίγα νερού κατευθείαν πάνω του.' },
                { q: 'Τι κάνω αν εμφανιστεί ερυθρότητα και πόνος;', a: 'Ελαφρά ερυθρότητα τις πρώτες 48 ώρες είναι φυσιολογική. Αν αυξάνεται ή συνοδεύεται από πόνο που επιδεινώνεται, θερμότητα ή πύον, επικοινωνήστε με ιατρό άμεσα.' },
                { q: 'Μπορώ να κάνω μπάνιο στη θάλασσα;', a: 'Όχι για τουλάχιστον 3 με 4 εβδομάδες. Το αλμυρό νερό και η άμμος ερεθίζουν το φρέσκο τατουάζ και αυξάνουν τον κίνδυνο μόλυνσης.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-2">{item.q}</h3>
                  <p className="text-gray-400 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Επικοινωνία <span className="text-pink-500">με το Studio</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Αν έχετε απορία για τη φροντίδα του τατουάζ σας ή θέλετε να κλείσετε ραντεβού για τατουάζ στην Αθήνα, στείλτε μήνυμα στο Instagram ή καλέστε στο <strong className="text-white">694 066 4049</strong>. Βρισκόμαστε στο Γκύζη και εξυπηρετούμε πελάτες από όλη την Αθήνα.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Internal links */}

        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-care-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Συντήρηση Τατουάζ στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Μακροπρόθεσμη φροντίδα και ο αθηναϊκός ήλιος: τι πρέπει να ξέρεις.</p>
              </Link>
              <Link to="/blog/first-tattoo-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Το Πρώτο σου Τατουάζ στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πλήρης οδηγός για πρωτάρηδες, από την επιλογή σχεδίου μέχρι την εκτέλεση.</p>
              </Link>
              <Link to="/blog/boro-gymnastiko-meta-tatouaz-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Μπορώ να Γυμναστώ μετά από Τατουάζ;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πόσο να περιμένεις και πώς να επιστρέψεις στη γυμναστική.</p>
              </Link>
              <Link to="/blog/boro-tatouaz-an-eimai-arrostos-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Τατουάζ όταν Είσαι Άρρωστος;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Γιατί χρειάζεται αναβολή και πώς να προγραμματίσεις σωστά.</p>
              </Link>
            </div>
          </div>
        </section>
        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
